import * as Yup from "yup";

/**
 * ── Phone number format ──────────────────────────────────────────────────────
 *
 * One rule for every phone number the platform accepts. The backend mirror
 * lives in `backend/utils/phone.js` — keep the two in step.
 *
 * The platform is international, so the rule cannot assume a national format:
 * "+372 5123 4567", "(020) 7946 0958" and "055-1234-567" are all the same kind
 * of thing. What it can insist on is that the value is *a phone number at all*
 * — which is what was missing when a directory happily stored "ABNKSANDI" in
 * the phone column.
 *
 * So: characters a phone number is written with, and a digit count inside the
 * range E.164 allows. Nothing stricter — a per-country check would reject real
 * numbers from countries nobody enumerated, which costs more than it saves.
 *
 * Deliberately NOT decided here: whether a phone number is required. Every
 * schema keeps its own optional/required rule and only asks this module "is
 * what the user typed a phone number?".
 */

/** Characters a written phone number may contain: digits, one leading "+", and separators. */
const PHONE_CHARS = /^\+?[\d\s().\-/]+$/;

/** E.164 caps a subscriber number at 15 digits; 7 is the shortest number in real use. */
export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 15;

/** A written number needs separators, but 25 characters is far past any of them. */
export const PHONE_MAX_LENGTH = 25;

/**
 * True when `value` is written like a phone number.
 *
 * Empty / null is *not* judged here — that is the schema's optional-vs-required
 * question — so blank values come back `true`.
 */
export const isValidPhoneFormat = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  const raw = String(value).trim();
  if (raw === "") return true;

  if (raw.length > PHONE_MAX_LENGTH) return false;
  if (!PHONE_CHARS.test(raw)) return false;

  // Parentheses come in pairs and only ever wrap an area/trunk code, so: one
  // pair, opened before it is closed, with an actual code inside it. "(0",
  // ")(", "((0))" and "()" are all typos rather than formats — the last one
  // matters because a balanced-and-ordered check alone lets "()" through, and
  // an empty bracket pair says nothing about the number.
  const opens = (raw.match(/\(/g) || []).length;
  const closes = (raw.match(/\)/g) || []).length;
  if (opens !== closes || opens > 1) return false;
  if (opens === 1) {
    const open = raw.indexOf("(");
    const close = raw.indexOf(")");
    if (open > close) return false;
    // Digits, optionally spaced. Never empty.
    if (!/^(?=.*\d)[\d\s]+$/.test(raw.slice(open + 1, close))) return false;
  }

  const digits = raw.replace(/\D/g, "").length;
  return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
};

/**
 * The optional-phone schema: nothing is made mandatory, but whatever is typed
 * has to be a number. `messageKey` exists only because the existing screens
 * already carry two translation keys for the same sentence.
 */
export const optionalPhoneSchema = (
  messageKey = "validation:partner.phoneInvalid",
) =>
  Yup.string()
    .nullable()
    .transform((value, original) => (original === "" ? null : value))
    .test("phone-format", messageKey, (value) => isValidPhoneFormat(value));
